// src/pages/Admin/Dashboard/MessagesSection.jsx
import React, { useEffect, useState } from "react";
import {
  getAllMessagesContactForm,
  markMessageContactForm,
  deleteMessageContactForm,
} from "../../../services/contactformServices";

import ButtonDelete from "../../../components/Button/ButtonDelete";
import ButtonEnvelopeToggle from "../../../components/Button/ButtonEnvelopeToggle";
import { FaEnvelope } from "react-icons/fa";

const MessagesSection = () => {
  const [messages, setMessages] = useState([]);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // Vérifie si l'utilisateur est en mode démo
  const checkUserRole = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user && user.role_id === 4) {
        setIsDemoMode(true);
      }
    } catch (e) {
      // ignore
    }
  };

  // Charge les messages
  const loadMessages = async () => {
    try {
      const data = await getAllMessagesContactForm();
      setMessages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des messages :", error);
    }
  };

  useEffect(() => {
    loadMessages();
    checkUserRole();
  }, []);

  const totalMessages = messages.length;
  const unreadMessages = messages.filter((m) => !m.read_message).length;

  // Gestion des actions désactivées en mode démo
  const handleDemoClick = () => {
    if (isDemoMode) {
      return true;
    }
    return false;
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // Marque un message comme lu/non lu
  const toggleReadStatus = async (messageId) => {
    if (handleDemoClick()) return;
    try {
      const message = messages.find((m) => m.id === messageId);
      if (!message) return;

      await markMessageContactForm(messageId);

      setMessages((prevMessages) =>
        prevMessages.map((m) =>
          m.id === messageId ? { ...m, read_message: !m.read_message } : m
        )
      );
    } catch (error) {
      console.error(
        "Erreur lors du basculement de l'état de lecture du message :",
        error
      );
    }
  };

  // Supprime un message
  const handleDeleteMessage = async (messageId) => {
    if (handleDemoClick()) return;
    try {
      await deleteMessageContactForm(messageId);
      setMessages((prevMessages) =>
        prevMessages.filter((msg) => msg.id !== messageId)
      );
      if (expandedId === messageId) setExpandedId(null);
    } catch (error) {
      console.error("Erreur lors de la suppression du message :", error);
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("fr-FR", {
      year: "2-digit",
      month: "2-digit",
      day: "2-digit",
    });

  const formatDateTime = (d) =>
    new Date(d).toLocaleString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 md:p-5 shadow-sm font-montserrat">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-sky-50 text-sky-600 flex items-center justify-center">
            <FaEnvelope className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Messages du formulaire de contact
            </h3>
            <p className="text-xs text-slate-500">
              {totalMessages} message(s) —{" "}
              <span className="font-semibold">{unreadMessages} non lu(s)</span>
            </p>
          </div>
        </div>
        {isDemoMode && (
          <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-semibold">
            Mode démonstration
          </span>
        )}
      </div>

      {messages.length > 0 ? (
        <div className="overflow-auto max-h-[28rem]">
          <table className="min-w-full text-sm bg-white dark:bg-slate-900">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-200">
                  Date
                </th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-200">
                  Message
                </th>
                <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-200">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {messages.map((message) => {
                const isExpanded = expandedId === message.id;

                return (
                  <React.Fragment key={message.id}>
                    <tr
                      className={
                        message.read_message
                          ? "bg-slate-50 dark:bg-slate-800/60"
                          : "bg-white dark:bg-slate-900"
                      }
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-700 dark:text-slate-100 align-top">
                        {formatDate(message.created_at)}
                      </td>

                      <td className="px-3 py-2 align-top text-xs text-slate-700 dark:text-slate-200">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 dark:text-slate-50 break-words">
                              {message.name}
                            </p>

                            {/* Aperçu court */}
                            <p className="mt-0.5 line-clamp-2 break-words">
                              {message.message}
                            </p>

                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                              <p className="text-[11px] text-slate-500 dark:text-slate-400 break-words">
                                {message.email}
                              </p>

                              <button
                                type="button"
                                onClick={() => toggleExpand(message.id)}
                                className="text-[11px] font-semibold text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300"
                              >
                                {isExpanded
                                  ? "Masquer le message"
                                  : "Voir le message complet"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap text-right align-top">
                        <div className="flex justify-end items-center gap-2">
                          <ButtonEnvelopeToggle
                            isRead={message.read_message}
                            onClick={() => toggleReadStatus(message.id)}
                          />
                          <ButtonDelete
                            onClick={() => handleDeleteMessage(message.id)}
                          />
                        </div>
                      </td>
                    </tr>

                    {/* Ligne détail : message complet */}
                    {isExpanded && (
                      <tr
                        className={
                          message.read_message
                            ? "bg-slate-50 dark:bg-slate-800/60"
                            : "bg-white dark:bg-slate-900"
                        }
                      >
                        <td colSpan={3} className="px-3 pb-3">
                          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-slate-900 dark:text-slate-100 break-words">
                                  {message.name}{" "}
                                  <span className="font-normal text-slate-500 dark:text-slate-400">
                                    — {message.email}
                                  </span>
                                </p>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                  Reçu le {formatDateTime(message.created_at)}
                                </p>
                              </div>
                            </div>

                            <div className="mt-2 max-h-64 overflow-y-auto pr-2 text-xs whitespace-pre-wrap break-words text-slate-700 dark:text-slate-200">
                              {message.message}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-slate-500 text-center py-6">
          Pas encore de messages reçus via le formulaire de contact.
        </p>
      )}
    </div>
  );
};

export default MessagesSection;
